/**
 * BulkAssignTable — the upload-sourced rows, every field proposed from the
 * file name and shown as inferred, editable before approval (plan §16.7
 * WP-95b, ctx-WP-95 PART G3). G8: a proposal is never applied without a
 * click — every pre-filled cell carries a `Proposed` tag, and the tag is
 * gone the instant the person edits that cell. Submits through
 * POST /intake/approve-bulk (PART A3): up to 25 rows in one request, each
 * succeeding or failing on its own, so a 409 on one row (WP-94's tag-
 * unresolved shape, `field` + `candidates`) never blocks the others.
 *
 * DOCUMENT_KIND_OPTIONS/DIRECTION_OPTIONS/NEGOTIATION_KIND_OPTIONS are
 * re-declared here rather than imported from AssignmentFields.tsx, which
 * this package may read and import but never edit ([OWNER] 238 covers the
 * one cross-tab-folder import this file does make, TagField).
 */
import { useEffect, useState } from 'react';
import { FilterSelect, fieldCls, primaryBtnCls } from '../../../ui';
import { approveIntakeBulk } from '../../api/intake';
import type {
  BulkApproveResult,
  BulkAssignment,
  DocumentKind,
  FilenameProposal,
  IntakeItem,
  NegotiationKind,
  VersionDirection,
} from '../../api/intake';
import { listNegotiations } from '../../api/client';
import type { Negotiation, Study, TagMatchCandidate } from '../../api/client';
import type { TagFieldState } from '../studies/TagField';
import { MasterTagField } from './MasterTagField';
import { tagUnresolvedMessage } from './AssignmentFields';

const BULK_APPROVE_MAX = 25;
const NEW_STUDY = '__new_study__';
const NEW_NEGOTIATION = '__new_negotiation__';

const DOCUMENT_KIND_OPTIONS: Array<{ value: DocumentKind; label: string }> = [
  { value: 'budget_workbook', label: 'Budget workbook' },
  { value: 'cta', label: 'CTA' },
  { value: 'budget_exhibit', label: 'Budget exhibit' },
  { value: 'protocol', label: 'Protocol' },
  { value: 'letter', label: 'Letter' },
  { value: 'email', label: 'Email' },
  { value: 'remittance', label: 'Remittance' },
  { value: 'other', label: 'Other' },
];

const DIRECTION_OPTIONS = [
  { value: '', label: 'Select direction…' },
  { value: 'received', label: 'Received' },
  { value: 'sent', label: 'Sent' },
  { value: 'internal', label: 'Internal' },
];

const NEGOTIATION_KIND_OPTIONS: Array<{ value: NegotiationKind; label: string }> = [
  { value: 'budget', label: 'Budget' },
  { value: 'contract', label: 'Contract' },
  { value: 'amendment_budget', label: 'Amendment (budget)' },
  { value: 'amendment_contract', label: 'Amendment (contract)' },
];

interface BulkRowState {
  selected: boolean;
  /** The proposal and studies list this row was last seeded from (PART D3):
   *  a pristine row is reseeded when either changes; a touched row never is. */
  seededProposal: FilenameProposal | null;
  seededStudiesKey: string;
  studyChoice: string; // NEW_STUDY, or an existing study id
  studyTouched: boolean;
  protocolNumber: string;
  protocolTouched: boolean;
  sponsor: TagFieldState;
  sponsorTouched: boolean;
  negotiationChoice: string; // NEW_NEGOTIATION, or an existing negotiation id
  negotiationKind: NegotiationKind;
  negotiationKindTouched: boolean;
  documentKind: DocumentKind;
  documentKindTouched: boolean;
  direction: VersionDirection | '';
  directionTouched: boolean;
  round: string;
  roundTouched: boolean;
  versionDate: string;
  versionDateTouched: boolean;
  result?: BulkApproveResult;
}

/** What this tag will send: the picked entry id, else the typed text, trimmed. Matches
 *  AssignmentFields.tsx's own tagValue — re-implemented here (PART G3: "re-implemented
 *  locally over the row model"), not imported (AssignmentFields.tsx does not export it). */
function tagValue(field: TagFieldState): string {
  return field.resolvedId ?? field.text.trim();
}

function makeInitialRowState(proposal: FilenameProposal | null, studies: Study[]): BulkRowState {
  // Pulled into plain locals before any lookup: a closure (studies.find's
  // callback below) cannot be relied on to keep TypeScript's narrowing of a
  // property access like `proposal.protocolNumber`, only of a const binding.
  const protocolValue = proposal?.protocolNumber?.value ?? null;
  const sponsorProposed = proposal?.sponsor ?? null;
  const sponsorCandidates = proposal?.sponsorCandidates ?? [];
  const matched = protocolValue != null ? studies.find((s) => s.protocol_number === protocolValue) ?? null : null;
  return {
    selected: false,
    seededProposal: proposal,
    seededStudiesKey: studies.map((s) => `${s.id}:${s.protocol_number}`).join('|'),
    studyChoice: matched ? matched.id : NEW_STUDY,
    studyTouched: false,
    protocolNumber: matched ? '' : protocolValue ?? '',
    protocolTouched: false,
    sponsor: sponsorProposed
      ? { text: sponsorProposed.display, resolvedId: sponsorProposed.value, candidates: null }
      : { text: '', resolvedId: null, candidates: sponsorCandidates.length > 0 ? sponsorCandidates : null },
    sponsorTouched: false,
    negotiationChoice: NEW_NEGOTIATION,
    negotiationKind: proposal?.negotiationKind?.value ?? 'budget',
    negotiationKindTouched: false,
    documentKind: proposal?.documentKind?.value ?? 'budget_workbook',
    documentKindTouched: false,
    direction: proposal?.direction?.value ?? '',
    directionTouched: false,
    round: proposal?.round ? String(proposal.round.value) : '',
    roundTouched: false,
    versionDate: proposal?.versionDate?.value ?? '',
    versionDateTouched: false,
  };
}

/** True while every field is still at its seeded value and the row has not
 *  yet been submitted (PART D2). `selected` does not count — checking a row
 *  never blocks a later reseed. */
function isPristine(row: BulkRowState): boolean {
  return (
    !row.studyTouched &&
    !row.protocolTouched &&
    !row.sponsorTouched &&
    !row.negotiationKindTouched &&
    !row.documentKindTouched &&
    !row.directionTouched &&
    !row.roundTouched &&
    !row.versionDateTouched &&
    !row.result
  );
}

/** Cannot submit a row until study, negotiation, document kind, direction, round and date are
 *  all set, and — for a new study — a sponsor (cta_studies.sponsor_id is NOT NULL). Mirrors
 *  AssignmentFields.tsx's assignmentIsComplete, re-implemented over this row's own shape. */
function rowIsComplete(row: BulkRowState): boolean {
  const studyOk =
    row.studyChoice !== NEW_STUDY
      ? row.studyChoice.trim().length > 0
      : row.protocolNumber.trim().length > 0 && tagValue(row.sponsor).length > 0;
  const negotiationOk = row.studyChoice === NEW_STUDY ? true : row.negotiationChoice.trim().length > 0;
  const roundOk = row.round.trim().length > 0 && Number.isInteger(Number(row.round));
  return studyOk && negotiationOk && row.direction.length > 0 && roundOk && row.versionDate.trim().length > 0;
}

function rowToAssignment(intakeItemId: string, row: BulkRowState): BulkAssignment {
  const studyPart =
    row.studyChoice !== NEW_STUDY
      ? { studyId: row.studyChoice }
      : {
          newStudy: {
            protocolNumber: row.protocolNumber.trim(),
            status: 'open',
            // ALWAYS sent, even empty: cta_studies.title is NOT NULL with no DEFAULT
            // (AssignmentFields.tsx's newStudyToInput does the same).
            title: '',
            sponsor: tagValue(row.sponsor),
          },
        };
  const negotiationPart =
    row.studyChoice !== NEW_STUDY && row.negotiationChoice !== NEW_NEGOTIATION
      ? { negotiationId: row.negotiationChoice }
      : { newNegotiation: { kind: row.negotiationKind, label: '' } };
  return {
    intakeItemId,
    ...studyPart,
    ...negotiationPart,
    documentKind: row.documentKind,
    direction: row.direction as VersionDirection,
    round: Number(row.round),
    versionDate: row.versionDate,
  };
}

/** The micro-label G8 requires on every pre-filled cell (PART G3, verbatim recipe). */
function ProposedTag({ rule, evidence }: { rule: string; evidence: string }) {
  return (
    <span
      className="ml-1.5 text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400"
      title={`Read from the file name: ${rule} — "${evidence}"`}
    >
      Proposed
    </span>
  );
}

interface RowProps {
  item: IntakeItem;
  studies: Study[];
  state: BulkRowState;
  onPatch: (partial: Partial<BulkRowState>) => void;
}

function BulkRow({ item, studies, state, onPatch }: RowProps) {
  const [negotiations, setNegotiations] = useState<Negotiation[] | null>(null);

  useEffect(() => {
    if (state.studyChoice === NEW_STUDY) {
      setNegotiations(null);
      return undefined;
    }
    let cancelled = false;
    listNegotiations(state.studyChoice)
      .then((rows) => {
        if (!cancelled) setNegotiations(rows);
      })
      .catch(() => {
        if (!cancelled) setNegotiations([]);
      });
    return () => {
      cancelled = true;
    };
  }, [state.studyChoice]);

  function patch(partial: Partial<BulkRowState>) {
    // Blocking fix: merge onto the LATEST row (handleRowPatch below), never
    // onto this render's `state` — MasterTagField's async match/add answer
    // can land after the person has ticked the checkbox or edited another
    // cell in this same window, and a stale-state write here would revert
    // that edit (PART J: "async onChange ... never the render-time closure").
    onPatch(partial);
  }

  function pickCandidate(candidate: TagMatchCandidate) {
    patch({
      sponsor: { text: candidate.entry.official_name, resolvedId: candidate.entry.id, candidates: null },
      sponsorTouched: true,
    });
  }

  const studyOptions = [
    { value: NEW_STUDY, label: 'New study' },
    ...studies.map((s) => ({ value: s.id, label: `${s.protocol_number}${s.title ? ` — ${s.title}` : ''}` })),
  ];

  const negotiationOptions = [
    { value: NEW_NEGOTIATION, label: 'New negotiation' },
    ...(negotiations ?? []).map((n) => ({ value: n.id, label: `${n.kind}${n.label ? ` — ${n.label}` : ''}` })),
  ];

  return (
    <tr data-testid={`cta-bulk-row-${item.id}`} className="align-top">
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={state.selected}
          onChange={(e) => patch({ selected: e.target.checked })}
          aria-label={`Select ${item.drive_name}`}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600"
        />
      </td>

      <td className="max-w-[16rem] truncate px-4 py-3 text-sm text-gray-900 dark:text-gray-100" title={item.drive_name}>
        {item.drive_name}
      </td>

      <td data-testid={`cta-bulk-cell-study-${item.id}`} className="min-w-[14rem] px-4 py-3">
        <FilterSelect
          label={`Study — ${item.drive_name}`}
          value={state.studyChoice}
          onChange={(v) => patch({ studyChoice: v, studyTouched: true, negotiationChoice: NEW_NEGOTIATION })}
          options={studyOptions}
        />
        {state.studyChoice !== NEW_STUDY && !state.studyTouched && state.seededProposal?.protocolNumber && (
          <ProposedTag rule={state.seededProposal.protocolNumber.rule} evidence={state.seededProposal.protocolNumber.evidence} />
        )}
        {state.studyChoice === NEW_STUDY && (
          <div className="mt-2 space-y-2">
            <div>
              <input
                id={`cta-bulk-protocol-${item.id}`}
                type="text"
                placeholder="Protocol number"
                value={state.protocolNumber}
                onChange={(e) => patch({ protocolNumber: e.target.value, protocolTouched: true })}
                className={`${fieldCls} w-full`}
              />
              {!state.protocolTouched && state.seededProposal?.protocolNumber && (
                <ProposedTag rule={state.seededProposal.protocolNumber.rule} evidence={state.seededProposal.protocolNumber.evidence} />
              )}
            </div>
            <div>
              <MasterTagField
                id={`cta-bulk-sponsor-${item.id}`}
                label="Sponsor"
                listType="sponsor"
                state={state.sponsor}
                onChange={(next) => patch({ sponsor: next, sponsorTouched: true })}
              />
              {!state.sponsorTouched && state.seededProposal?.sponsor && (
                <ProposedTag rule={state.seededProposal.sponsor.rule} evidence={state.seededProposal.sponsor.evidence} />
              )}
            </div>
          </div>
        )}
      </td>

      <td data-testid={`cta-bulk-cell-negotiation-${item.id}`} className="min-w-[12rem] px-4 py-3">
        {state.studyChoice === NEW_STUDY ? (
          <>
            <FilterSelect
              label={`Negotiation kind — ${item.drive_name}`}
              value={state.negotiationKind}
              onChange={(v) => patch({ negotiationKind: v as NegotiationKind, negotiationKindTouched: true })}
              options={NEGOTIATION_KIND_OPTIONS}
            />
            {!state.negotiationKindTouched && state.seededProposal?.negotiationKind && (
              <ProposedTag rule={state.seededProposal.negotiationKind.rule} evidence={state.seededProposal.negotiationKind.evidence} />
            )}
          </>
        ) : (
          <FilterSelect
            label={`Negotiation — ${item.drive_name}`}
            value={state.negotiationChoice}
            onChange={(v) => patch({ negotiationChoice: v })}
            options={negotiationOptions}
          />
        )}
      </td>

      <td data-testid={`cta-bulk-cell-dockind-${item.id}`} className="min-w-[10rem] px-4 py-3">
        <FilterSelect
          label={`Document kind — ${item.drive_name}`}
          value={state.documentKind}
          onChange={(v) => patch({ documentKind: v as DocumentKind, documentKindTouched: true })}
          options={DOCUMENT_KIND_OPTIONS}
        />
        {!state.documentKindTouched && state.seededProposal?.documentKind && (
          <ProposedTag rule={state.seededProposal.documentKind.rule} evidence={state.seededProposal.documentKind.evidence} />
        )}
      </td>

      <td data-testid={`cta-bulk-cell-direction-${item.id}`} className="min-w-[9rem] px-4 py-3">
        <FilterSelect
          label={`Direction — ${item.drive_name}`}
          value={state.direction}
          onChange={(v) => patch({ direction: v as VersionDirection | '', directionTouched: true })}
          options={DIRECTION_OPTIONS}
        />
        {!state.directionTouched && state.seededProposal?.direction && (
          <ProposedTag rule={state.seededProposal.direction.rule} evidence={state.seededProposal.direction.evidence} />
        )}
      </td>

      <td data-testid={`cta-bulk-cell-round-${item.id}`} className="w-24 px-4 py-3">
        <input
          id={`cta-bulk-round-${item.id}`}
          type="number"
          min={0}
          step={1}
          value={state.round}
          onChange={(e) => patch({ round: e.target.value, roundTouched: true })}
          className={`${fieldCls} w-full`}
        />
        {!state.roundTouched && state.seededProposal?.round && <ProposedTag rule={state.seededProposal.round.rule} evidence={state.seededProposal.round.evidence} />}
      </td>

      <td data-testid={`cta-bulk-cell-versiondate-${item.id}`} className="w-40 px-4 py-3">
        <input
          id={`cta-bulk-versiondate-${item.id}`}
          type="date"
          value={state.versionDate}
          onChange={(e) => patch({ versionDate: e.target.value, versionDateTouched: true })}
          className={`${fieldCls} w-full`}
        />
        {!state.versionDateTouched && state.seededProposal?.versionDate && (
          <ProposedTag rule={state.seededProposal.versionDate.rule} evidence={state.seededProposal.versionDate.evidence} />
        )}
      </td>

      <td data-testid={`cta-bulk-result-${item.id}`} className="min-w-[11rem] px-4 py-3">
        {state.result && state.result.ok && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/20 dark:text-green-400">
            Approved
          </span>
        )}
        {state.result && !state.result.ok && (
          <div className="space-y-1.5">
            <p className="text-xs text-red-600 dark:text-red-400">
              {state.result.field ? tagUnresolvedMessage(state.result.field) : state.result.error}
            </p>
            {state.result.candidates && state.result.candidates.length > 0 && (
              <div className="space-y-1">
                {state.result.candidates.map((c) => (
                  <button
                    key={c.entry.id}
                    type="button"
                    onClick={() => pickCandidate(c)}
                    className="block w-full rounded px-1.5 py-0.5 text-left text-xs font-medium text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20"
                  >
                    {c.entry.official_name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

interface Props {
  rows: Array<{ item: IntakeItem; proposal: FilenameProposal | null }>;
  studies: Study[];
  onApproved: (results: BulkApproveResult[]) => void;
}

export function BulkAssignTable({ rows, studies, onApproved }: Props) {
  const [rowStates, setRowStates] = useState<Record<string, BulkRowState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [envelopeError, setEnvelopeError] = useState<string | null>(null);

  // New rows get an initial state seeded from their proposal. A row already
  // known here is reseeded from a fresh proposal or studies list ONLY while
  // it is still pristine (item 262: this effect used to seed a row once,
  // while its backfilled proposal was still null, so the proposal that
  // arrived a moment later never reached the cells) — a touched row keeps
  // its values and gets no late tags; G8's proposal is only ever a STARTING
  // point, never re-applied over a person's own edit.
  useEffect(() => {
    const studiesKey = studies.map((s) => `${s.id}:${s.protocol_number}`).join('|');
    setRowStates((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const { item, proposal } of rows) {
        const existing = next[item.id];
        if (!existing) {
          next[item.id] = makeInitialRowState(proposal, studies);
          changed = true;
        } else if (isPristine(existing) && (existing.seededProposal !== proposal || existing.seededStudiesKey !== studiesKey)) {
          next[item.id] = { ...makeInitialRowState(proposal, studies), selected: existing.selected };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rows, studies]);

  function handleRowPatch(id: string, partial: Partial<BulkRowState>) {
    setRowStates((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], ...partial } } : prev));
  }

  const selectedIds = rows.filter(({ item }) => rowStates[item.id]?.selected).map(({ item }) => item.id);
  const overCap = selectedIds.length > BULK_APPROVE_MAX;
  const allComplete = selectedIds.length > 0 && selectedIds.every((id) => rowIsComplete(rowStates[id]));
  const submitDisabled = selectedIds.length === 0 || overCap || !allComplete || submitting;

  function toggleSelectAll(checked: boolean) {
    setRowStates((prev) => {
      const next = { ...prev };
      for (const { item } of rows) {
        if (next[item.id]) next[item.id] = { ...next[item.id], selected: checked };
      }
      return next;
    });
  }

  async function handleApproveSelected() {
    if (submitDisabled) return;
    setSubmitting(true);
    setEnvelopeError(null);
    try {
      const assignments: BulkAssignment[] = selectedIds.map((id) => rowToAssignment(id, rowStates[id]));
      const response = await approveIntakeBulk(assignments);
      setRowStates((prev) => {
        const next = { ...prev };
        for (const result of response.results) {
          // WP-95b fix (blocking defect 1): a succeeded row is deselected,
          // never removed, so a re-submit sends only the rows still needing
          // one (PART G3: "re-submits only that row") while the Approved row
          // stays visible showing its own result — IntakeTab.handleBulkApproved
          // no longer drops it out of the table in that same render.
          if (next[result.intakeItemId]) {
            next[result.intakeItemId] = {
              ...next[result.intakeItemId],
              result,
              selected: result.ok ? false : next[result.intakeItemId].selected,
            };
          }
        }
        return next;
      });
      onApproved(response.results);
    } catch (err) {
      const res = (err as { response?: { data?: { error?: string } } })?.response;
      setEnvelopeError(res?.data?.error ?? 'Something went wrong approving these files.');
    } finally {
      setSubmitting(false);
    }
  }

  if (rows.length === 0) return null;

  const allSelected = rows.length > 0 && rows.every(({ item }) => rowStates[item.id]?.selected);

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Values marked Proposed were read from the file name. Nothing is approved until you click Approve selected.
      </p>

      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <table className="w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  aria-label="Select all files"
                  data-testid="cta-bulk-select-all"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600"
                />
              </th>
              {['File', 'Study', 'Negotiation', 'Document kind', 'Direction', 'Round', 'Version date', 'Result'].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map(({ item }) => {
              const state = rowStates[item.id];
              if (!state) return null;
              return (
                <BulkRow
                  key={item.id}
                  item={item}
                  studies={studies}
                  state={state}
                  onPatch={(partial) => handleRowPatch(item.id, partial)}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {envelopeError && <p className="text-sm text-red-600 dark:text-red-400">{envelopeError}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" data-testid="cta-bulk-approve" disabled={submitDisabled} onClick={handleApproveSelected} className={primaryBtnCls}>
          {submitting ? 'Approving…' : 'Approve selected'}
        </button>
        <span className="text-xs text-gray-500 dark:text-gray-400">{selectedIds.length} selected</span>
        {overCap && (
          <span className="text-xs font-medium text-red-600 dark:text-red-400">Select at most 25 files per approval.</span>
        )}
      </div>
    </div>
  );
}

export default BulkAssignTable;
